import React, { useState, useEffect } from "react";
import { Row, Col, Button, Container } from "react-bootstrap";
import {
  FaBook,
  FaClock,
  FaCreditCard,
  FaArrowRight,
  FaCheckCircle,
  FaPlayCircle,
  FaRupeeSign,
  FaPercent,
  FaUsers,
  FaGraduationCap,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import StudentService from "../../services/studentService";
import Header from "../../components/common/Header";
import ContactModal from "../../components/common/ContactModal";

import "../../styles/myCourses.css";

const MyCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await StudentService.getStudentCourses(user.id);
      console.log("Courses with batch info:", response.data);
      setCourses(response.data || []);
    } catch (error) {
      toast.error("Failed to load courses");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCourse = (courseId) => {
    navigate(`/student/courses/${courseId}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Get effective fee after discount
  const getEffectiveFee = (course) => {
    return course.total_fees - (course.discount_amount || 0);
  };

  // Get due amount after discount
  const getDueAmount = (course) => {
    const effectiveFee = getEffectiveFee(course);
    return effectiveFee - (course.paid_amount || 0);
  };

  // Calculate payment progress percentage
  const getPaymentProgress = (course) => {
    const effectiveFee = getEffectiveFee(course);
    if (effectiveFee <= 0) return 100;
    return Math.round((course.paid_amount / effectiveFee) * 100);
  };

  // Truncate description to fixed length
  const truncateDescription = (text, maxLength = 80) => {
    if (!text) return "No description available";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Get progress color based on percentage
  const getProgressColor = (progress) => {
    if (progress >= 75) return "#28a745";
    if (progress >= 50) return "#17a2b8";
    if (progress >= 25) return "#ffc107";
    return "#dc3545";
  };

  // Statistics
  const totalCourses = courses.length;
  const completedCourses = courses.filter((c) => getDueAmount(c) === 0).length;
  const totalPaid = courses.reduce((sum, c) => sum + (c.paid_amount || 0), 0);
  const totalDue = courses.reduce((sum, c) => sum + getDueAmount(c), 0);

  if (loading) {
    return (
      <div>
        <Header />
        <div className="mycourses-loading">
          <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading your courses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="mycourses-page">
        {/* Hero Section */}
        <section className="mycourses-hero">
          <Container>
            <div className="hero-content">
              <h1 className="hero-title">
                My Learning Journey
                <span className="hero-emoji">📚</span>
              </h1>
              <p className="hero-subtitle">
                Track your progress and continue your learning journey
              </p>
            </div>
          </Container>
          <div className="hero-wave">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1440 120"
              preserveAspectRatio="none"
            >
              <path
                fill="#ffffff"
                fillOpacity="1"
                d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
              ></path>
            </svg>
          </div>
        </section>

        <Container className="mycourses-container">
          {/* Statistics Cards */}
          <Row className="stats-row mb-5">
            <Col lg={3} md={6} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-primary-soft">
                  <FaBook />
                </div>
                <div className="stat-info">
                  <h3>{totalCourses}</h3>
                  <p>Total Courses</p>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-success-soft">
                  <FaCheckCircle />
                </div>
                <div className="stat-info">
                  <h3>{completedCourses}</h3>
                  <p>Completed</p>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-info-soft">
                  <FaRupeeSign />
                </div>
                <div className="stat-info">
                  <h3>{formatCurrency(totalPaid)}</h3>
                  <p>Total Paid</p>
                </div>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <div className="stat-card">
                <div className="stat-icon bg-warning-soft">
                  <FaPercent />
                </div>
                <div className="stat-info">
                  <h3>{formatCurrency(totalDue)}</h3>
                  <p>Due Amount</p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Courses Grid */}
          {courses.length === 0 ? (
            <div className="empty-state">
              <FaBook size={60} className="empty-icon" />
              <h4>No Courses Enrolled Yet</h4>
              <p>Browse our courses and start your learning journey!</p>
              <Button variant="primary" onClick={() => navigate("/student")}>
                Browse Courses <FaArrowRight className="ms-2" />
              </Button>
            </div>
          ) : (
            <>
              <div className="section-header">
                <h2 className="section-title">
                  <FaPlayCircle className="me-2 text-primary" />
                  My Enrolled Courses
                </h2>
                <span className="section-sub">
                  Continue learning where you left off
                </span>
              </div>

              <Row className="courses-grid">
                {courses.map((course) => {
                  const effectiveFee = getEffectiveFee(course);
                  const dueAmount = getDueAmount(course);
                  const paymentProgress = getPaymentProgress(course);
                  const isFullyPaid = dueAmount === 0;
                  const hasBatch =
                    course.batch_id !== null && course.batch_name !== null;
                  const progressColor = getProgressColor(paymentProgress);

                  return (
                    <Col lg={6} xl={4} key={course.course_id} className="mb-4">
                      <div className="modern-course-card">
                        {/* Card Image Section */}
                        <div className="card-image-wrapper">
                          {course.course_image ? (
                            <img
                              src={course.course_image}
                              alt={course.course_name}
                              className="card-image"
                            />
                          ) : (
                            <div className="card-image-placeholder">
                              <FaGraduationCap size={48} />
                            </div>
                          )}
                          {/* Status Badge */}
                          <div
                            className={`status-badge ${isFullyPaid ? "paid" : "pending"}`}
                          >
                            {isFullyPaid ? (
                              <>
                                <FaCheckCircle /> Paid
                              </>
                            ) : (
                              <>
                                <FaCreditCard /> Pending
                              </>
                            )}
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="card-content">
                          <h3 className="course-title">{course.course_name}</h3>
                          <p className="course-description">
                            {truncateDescription(course.description, 85)}
                          </p>

                          {/* Batch Info */}
                          {hasBatch && (
                            <div className="batch-info">
                              <div className="batch-chip">
                                <FaUsers className="batch-icon" />
                                <span>{course.batch_name}</span>
                              </div>
                              {/* {course.batch_progress !== null && (
                                <div className="batch-progress">
                                  <div className="batch-progress-bar">
                                    <div
                                      className="batch-progress-fill"
                                      style={{
                                        width: `${course.batch_progress}%`,
                                      }}
                                    />
                                  </div>
                                  <span className="batch-progress-text">
                                    {course.batch_progress}%
                                  </span>
                                </div>
                              )} */}
                            </div>
                          )}

                          {/* Course Meta */}
                          <div className="course-meta">
                            <div className="meta-item">
                              <FaClock className="meta-icon" />
                              <span>{course.duration || "Self-paced"}</span>
                            </div>
                            <div className="meta-item">
                              <FaRupeeSign className="meta-icon" />
                              <span>{formatCurrency(effectiveFee)}</span>
                            </div>
                          </div>

                          {/* Payment Progress Ring and Summary */}
                          <div className="payment-section">
                            <div className="payment-ring">
                              <svg
                                className="progress-ring"
                                width="60"
                                height="60"
                              >
                                <circle
                                  className="progress-ring-bg"
                                  stroke="#e0e0e0"
                                  strokeWidth="4"
                                  fill="transparent"
                                  r="26"
                                  cx="30"
                                  cy="30"
                                />
                                <circle
                                  className="progress-ring-fill"
                                  stroke={progressColor}
                                  strokeWidth="4"
                                  fill="transparent"
                                  r="26"
                                  cx="30"
                                  cy="30"
                                  strokeDasharray={`${2 * Math.PI * 26}`}
                                  strokeDashoffset={`${2 * Math.PI * 26 * (1 - paymentProgress / 100)}`}
                                  transform="rotate(-90 30 30)"
                                />
                                <text
                                  x="30"
                                  y="36"
                                  textAnchor="middle"
                                  fontSize="12"
                                  fontWeight="bold"
                                  fill={progressColor}
                                >
                                  {paymentProgress}%
                                </text>
                              </svg>
                            </div>
                            <div className="payment-summary">
                              <div className="summary-row">
                                <span className="summary-label">Paid:</span>
                                <span className="summary-value paid">
                                  {formatCurrency(course.paid_amount)}
                                </span>
                              </div>
                              <div className="summary-row">
                                <span className="summary-label">Due:</span>
                                <span
                                  className={`summary-value ${dueAmount > 0 ? "due" : ""}`}
                                >
                                  {formatCurrency(dueAmount)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Action Button */}
                          <button
                            className="view-course-btn"
                            onClick={() => handleViewCourse(course.course_id)}
                          >
                            <span>View Course</span>
                            <FaArrowRight className="btn-icon" />
                          </button>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Container>

        {/* Contact Modal */}
        <ContactModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          courseName={selectedCourse?.course_name}
          courseId={selectedCourse?.course_id}
        />
      </div>
    </div>
  );
};

export default MyCourses;
